import { Avatar, Box, Chip, MenuItem, Typography } from "@mui/material";
import { StandardCard } from "../../../components/Cards";
import { DataTable } from "../../../components/Table/DataTable";
import {
  IDataTableProps,
  MobileLogoRenderType,
  RenderType,
} from "../../../components/Table/interfaces/IDataTableProps";
import { PageContainer } from "../../../components/container/PageContainer";
import { LoadState, MODULE_IDS } from "../../../constants/enums";
import { RoleBasedCustomButton } from "../../../security/RoleBasedComponents/RoleBasedCustomButton/RoleBasedCustomButton";
import { useEffect, useState } from "react";
import { useDispatchWrapper } from "../../../hooks";
import { IStoreState } from "../../../redux";
import { useSelector } from "react-redux";
import {
  IBranch,
  IZone,
} from "../../../redux/DataManagement/dataManagement.types";
import {
  defaultUserBranch,
  defaultUserZone,
} from "../../../redux/DataManagement/defaultState";
import {
  clearUserBranch,
  clearUserZone,
  clearUserZoneState,
  fetchUserBranchListAsync,
  fetchUserZoneListAsync,
} from "../../../redux/DataManagement/dataManagementActions";
import { CustomSelect } from "../../../components/formsComponents";
import { DateFilterDropdown } from "../../../components/Table/components/DateFilterDropdown/DateFilterDropdown";
import { useDateFilter } from "../../../components/Table/hooks/useDateFilter";
import { CustomDatePicker } from "../../../components/formsComponents/CustomDatePicker";
import moment from "moment";
import {
  CSVExportPlugin,
  RefreshPlugin,
  SearchPlugin,
} from "../../../components/Table/plugins";
import { StandardTableActions } from "../../../components/StandardTableActions/StandardTableActions";
import { ManageZoneDialog } from "./ManageZoneDialog";
import { StatusRenderer } from "../../../components/ActiveInActiveStatus/ActiveInActiveStatus";
import { CustomAccordion } from "../../../components/CustomAccordion/CustomAccordion";

export const ZoneList: React.FC = () => {
  const [openCreateZone, setOpenCreateZone] = useState(false);
  const [zoneData, setZoneData] = useState<IZone>(defaultUserZone);
  const handleToggleCreate = () => {
    setZoneData(defaultUserZone);
    setOpenCreateZone(!openCreateZone);
  };
  const handleComplete = () => {
    fetchList();
    setOpenCreateZone(false);
  };
  const dispatch = useDispatchWrapper();

  const { list, totalRecords, loading } = useSelector(
    (storeState: IStoreState) => storeState.dataManagement.zoneList,
  );

  const [status, setStatus] = useState<IBranch["status"] | "-1">("-1");
  const [searchColumns, setSearchColumns] = useState<string[]>([]);
  const [search, setSearch] = useState<string>("");
  const [pagination, setPagination] = useState<{
    pageNumber: number;
    rowsPerPage: number;
  }>({ pageNumber: 1, rowsPerPage: 10 });
  const { date, type, handleDateSelectChange, dateDropdownList, setDate } =
    useDateFilter({ defaultType: "last28Days" });

  const zoneListProps: IDataTableProps = {
    isPagination: true,
    totalRecords: totalRecords,
    rowsPerPageOptions: pagination.rowsPerPage,
    isDataLoading: loading !== LoadState.Loaded,
    uniqueRowKeyName: "id",
    tableCommandBarProps: {
      leftItems: {
        customPlugins: [
          {
            key: "status",
            onRender: () => (
              <CustomSelect
                sx={{ minWidth: "120px" }}
                value={status}
                onChange={(e: any) => setStatus(e.target.value as "ACTIVE")}
              >
                <MenuItem value="-1">All Status</MenuItem>
                <MenuItem value="HOT">Hot</MenuItem>
                <MenuItem value="COLD">Cold</MenuItem>
                <MenuItem value="WARM">Warm</MenuItem>
              </CustomSelect>
            ),
          },
          {
            key: "byDate",
            onRender: () => (
              <DateFilterDropdown
                value={type}
                options={dateDropdownList}
                onChange={handleDateSelectChange}
              />
            ),
          },
          {
            key: "fromDate",
            onRender: () =>
              type === "custom" ? (
                <CustomDatePicker
                  sx={{ minWidth: "150px" }}
                  value={date.fromDate}
                  onChange={(newValue) => {
                    const newDate = moment(newValue).format("YYYY-MM-DD");
                    setDate({ ...date, fromDate: newDate });
                  }}
                />
              ) : (
                <></>
              ),
          },
          {
            key: "toDate",
            onRender: () =>
              type === "custom" ? (
                <CustomDatePicker
                  sx={{ minWidth: "150px" }}
                  value={date.toDate}
                  onChange={(newValue) => {
                    const newDate = moment(newValue).format("YYYY-MM-DD");
                    setDate({ ...date, toDate: newDate });
                  }}
                />
              ) : (
                <></>
              ),
          },
          {
            key: "csvExport",
            onRender: (columns, items) => (
              <CSVExportPlugin
                columns={columns}
                items={items}
                filePrefixName="Report"
              />
            ),
          },
          {
            key: "refresh",
            onRender: () => <RefreshPlugin onClick={fetchList} />,
          },
        ],
      },
      rightItems: {
        plugins: {},
        customPlugins: [
          {
            key: "search",
            onRender: () => {
              return (
                <SearchPlugin
                  selectedDropdownValue={searchColumns}
                  dropdownOptions={[{ label: "Zone Name", value: "zone_name" }]}
                  onDropdownChange={(value) => setSearchColumns(value)}
                  onChange={(newValue) => {
                    setSearch(newValue);
                  }}
                />
              );
            },
          },
        ],
      },
    },
    mobileLogo: {
      type: MobileLogoRenderType.reactNode,
      onMobileLogoRender: (data: any) => {
        return (
          <Avatar
            sx={{
              width: 45,
              height: 45,
              color: "#fff",
              ml: "-8px",
              mr: 2,
            }}
          ></Avatar>
        );
      },
    },
    columns: [
      {
        key: "status",
        headerName: "Status",
        fieldName: "status",
        exportCellWidth: 30,
        renderType: RenderType.CHIP_WARNING,
        onRowCellRender: (value, row: IZone) => {
          return <StatusRenderer status={row.status} />;
        },
      },
      {
        key: "zone_name",
        headerName: "Zone Name",
        fieldName: "zone_name",
        exportCellWidth: 40,
        renderType: RenderType.CUSTOM_RENDER,
      },
      {
        key: "branch_name",
        headerName: "Branch",
        fieldName: "branch_name",
        exportCellWidth: 40,
        renderType: RenderType.CUSTOM_RENDER,
        onRowCellRender: (value, row) => {
          return (
            <CustomAccordion title="Branch List" pannelId="user_branch" isLight>
              <Box sx={{ display: "flex", gap: 0.5 }}>
                {row.branches_uuid.length > 0 &&
                  row.branches_uuid.map((singleBranch: IBranch) => (
                    <Chip
                      key={singleBranch.branch_uuid}
                      label={singleBranch.branch_name}
                    />
                  ))}
              </Box>
            </CustomAccordion>
          );
        },
      },
      {
        key: "view",
        headerName: "Actions",
        fieldName: "",
        renderType: RenderType.CUSTOM_RENDER,
        onRowCellRender: (value, row) => {
          return (
            <StandardTableActions
              commentBoxTypeId={row.branch_uuid}
              onEditClick={() => {
                setZoneData(row);
                setOpenCreateZone(true);
              }}
            />
          );
        },
      },
    ],
    items: list,
    onPageChange: (newPageNumber: number) => {
      setPagination({ ...pagination, pageNumber: newPageNumber });
    },
    onRowsPerPageChange: (pageNumber: number, rowsPerPage: number) => {
      setPagination({
        pageNumber: pageNumber,
        rowsPerPage: rowsPerPage,
      });
    },
  };
  const fetchList = () => {
    dispatch(
      fetchUserZoneListAsync({
        status: status,
        page: pagination.pageNumber,
        rowsPerPage: pagination.rowsPerPage,
        columns: searchColumns,
        value: search,
        fromDate: date.fromDate,
        toDate: date.toDate,
      }),
    );
  };

  useEffect(() => {
    fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination, date, status, search, date]);

  useEffect(() => {
    return () => {
      dispatch(clearUserZoneState());
    };
  }, []);

  useEffect(() => {
    return () => {
      dispatch(clearUserZone());
    };
  }, []);
  return (
    <PageContainer
      title="Data management list"
      description="this is inner page"
    >
      <StandardCard>
        <Box
          display={"flex"}
          justifyContent={"space-between"}
          sx={{
            mx: 5,
            my: 2,
          }}
        >
          <Typography variant="h2" fontWeight={"700"}>
            Zone List
          </Typography>
          <RoleBasedCustomButton
            moduleId={MODULE_IDS.USERS}
            variant="contained"
            onClick={handleToggleCreate}
          >
            Add Zone
          </RoleBasedCustomButton>
        </Box>

        <DataTable {...zoneListProps} />
      </StandardCard>
      {openCreateZone && (
        <ManageZoneDialog
          open={openCreateZone}
          onComplete={handleComplete}
          onClose={handleToggleCreate}
          zoneData={zoneData}
        />
      )}
    </PageContainer>
  );
};
